<script setup lang="ts">
import {computed, ref} from 'vue';
import {ElInput, ElRadioButton, ElRadioGroup, ElSwitch} from 'element-plus';
import {FASE_KLEUR, INITIATIEF_FASE_VOLGORDE, InitiatiefFaseEnum_name, TijdlijnCel, TijdlijnRij} from 'common';

const STANDAARD_AANTAL: number = 15;

type Sortering = 'leeftijd' | 'fase' | 'fte';

const props = defineProps<{
    rijen: TijdlijnRij[];
    kwartalen: string[];
}>();

const sortering = ref<Sortering>('leeftijd');
const zoek      = ref<string>('');
const alles     = ref<boolean>(false);

const gesorteerd = computed<TijdlijnRij[]>(() => {
    const z: string           = zoek.value.trim().toLowerCase();
    const gevonden: TijdlijnRij[] = props.rijen.filter(r => z === '' || r.label.toLowerCase().includes(z));
    const sleutel = (r: TijdlijnRij): number => sortering.value === 'leeftijd' ? r.leeftijd : sortering.value === 'fase' ? r.kwartalenInHuidigeFase : r.fte;
    const alle: TijdlijnRij[] = [...gevonden].sort((a, b) => sleutel(b) - sleutel(a) || a.label.localeCompare(b.label));
    return alles.value ? alle : alle.slice(0, STANDAARD_AANTAL);
});
const onderbroken = computed<number>(() => props.rijen.filter(r => r.onderbroken).length);
const teruggezet  = computed<number>(() => props.rijen.filter(r => r.teruggezet).length);

// a row's cell for a timeline column, or undefined before the initiative's first quarter
function celVan(rij: TijdlijnRij, kwartaal: string): TijdlijnCel | undefined {
    return rij.cellen.find(c => c.kwartaal === kwartaal);
}

// labels that occur twice get their organisation appended
const labelVan = computed<Map<string, string>>(() => {
    const telling: Map<string, number> = new Map();
    for (const r of props.rijen) {
        telling.set(r.label, (telling.get(r.label) ?? 0) + 1);
    }
    return new Map(props.rijen.map(r => [r.id, (telling.get(r.label) ?? 0) > 1 ? `${r.label} - ${r.organisatie}` : r.label] as [string, string]));
});
</script>

<template>
    <div class="flex flex-col gap-2">
        <div class="flex flex-row flex-wrap items-center gap-4">
            <el-radio-group v-model="sortering">
                <el-radio-button value="leeftijd">Leeftijd</el-radio-button>
                <el-radio-button value="fase">Kwartalen in huidige fase</el-radio-button>
                <el-radio-button value="fte">FTE-impact</el-radio-button>
            </el-radio-group>
            <el-input v-model="zoek" placeholder="Zoek initiatief" clearable class="w-64"/>
            <el-switch v-model="alles" active-text="Toon alle initiatieven"/>
            <span class="text-sm text-gray-500">{{ gesorteerd.length }} van {{ rijen.length }}</span>
        </div>
        <div class="flex flex-row flex-wrap items-center gap-3 text-xs text-gray-600">
            <span v-for="f in INITIATIEF_FASE_VOLGORDE" :key="f" class="flex items-center gap-1"><span class="inline-block w-3 h-3 rounded-sm" :style="{backgroundColor: FASE_KLEUR[f]}"/>{{ InitiatiefFaseEnum_name(f) }}</span>
            <span>Aandachtspunten in deze selectie: {{ onderbroken }} onderbroken, {{ teruggezet }} teruggezet.</span>
        </div>
        <div class="overflow-x-auto">
            <table class="text-xs border-collapse">
                <thead>
                    <tr>
                        <th class="text-left pr-4 py-1 font-semibold">Initiatief</th>
                        <th v-for="q in kwartalen" :key="q" class="px-1 py-1 font-normal text-gray-500 whitespace-nowrap">{{ q }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="r in gesorteerd" :key="r.id" data-testid="timeline-row">
                        <td class="pr-4 py-0.5 whitespace-nowrap max-w-md truncate" :title="`${r.organisatie}: ${r.label}`">
                            {{ labelVan.get(r.id) }}
                            <span v-if="r.teruggezet" class="inline-block w-2 h-2 rounded-full ml-1" style="background-color: #e85c5c" title="teruggezet"/>
                        </td>
                        <td v-for="q in kwartalen" :key="q" class="px-1 py-0.5">
                            <div v-if="celVan(r, q)"
                                 class="h-5 w-14 rounded-sm"
                                 :style="{backgroundColor: FASE_KLEUR[celVan(r, q)!.fase], opacity: celVan(r, q)!.onderbroken ? 0.42 : 1}"
                                 :title="`${q}: ${InitiatiefFaseEnum_name(celVan(r, q)!.fase)}${celVan(r, q)!.onderbroken ? ' (onderbroken)' : ''}`"
                            />
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>
